import { MoneyValues, ShopStyle, ServiceFee, Event, FormComponent, OrderedList, PaymentMethod } from "./TicketApi";


export type Listing = {
    id: string;
    price: MoneyValues;
    fee: ServiceFee;
    available: number;
    product_id: string;
    product_name: string;
    product_type?: {
        type: 'simple' | 'grouped_product';
        products?: {
            productId: string;
            quantity: number;
            price: MoneyValues;
            serviceFee: ServiceFee | null;
            name: string;
            description: string | null;
        }[];
    };
}

export type Marketplace = {
    name: string;
    style: ShopStyle;
    event: Event;
    slug: string;
    listings: Listing[];
    forSaleCount: number;
    soldCount: number;
    waitlistCount: number;
    form: OrderedList<FormComponent>;
    paymentMethods: PaymentMethod[];
}

export interface CartItem {
    listingId: string;
    quantity: number;
    price: MoneyValues;
    fee: ServiceFee;
}

export interface CartState {
    cartId: string;
    reserved: CartItem[];
    answers: object;
    pendingPayment: boolean;
    orderId: string|null;
    listings: Listing[];
    timesOutAtUtc?: string;
}

export interface CartStateWithErrors extends CartState {
    error: string;
}

export class MarketplaceApi {
    constructor(
        private readonly baseUrl: string,
    ) {
    }

    public async getMarketplace(slug: string): Promise<Marketplace> {
        const res = await fetch(`${this.baseUrl}/marketplace/${slug}`, {cache: "no-cache"});
        return await res.json();
    }

    public async getCart(slug: string, cartId: string, language?: string): Promise<CartState|CartStateWithErrors> {
        try {
            const url = new URL(`${this.baseUrl}/marketplace/${slug}/cart/${cartId}`);
            if (language) {
                url.searchParams.set('language', language);
            }
            
            const response = await fetch(url.toString(), {
                headers: {
                    'Accept': 'application/json',
                },
            });
            if(response.ok) {
                const data = await response.json();
                if (data.error === 'timed-out') {
                    return {
                        reserved: [],
                        cartId: cartId,
                        answers: {},
                        pendingPayment: false,
                        orderId: null,
                        listings: [],
                        error: 'timed-out'
                    };
                }
                return data;
            } else {
                return {
                    reserved: [],
                    cartId: cartId,
                    answers: {},
                    pendingPayment: false,
                    orderId: null,
                    listings: [],
                };
            }
        } catch (error) {
            console.error('Failed to get cart:', error);
            return {
                reserved: [],
                cartId: cartId,
                answers: {},
                pendingPayment: false,
                orderId: null,
                listings: [],
            };
        }
    }

    public async reserveListing(slug: string, cartId: string, listingId: string, quantity: number, language?: string): Promise<CartState|CartStateWithErrors> {
        try {
            const url = new URL(`${this.baseUrl}/marketplace/${slug}/cart/${cartId}/reserve-listing`);
            if (language) {
                url.searchParams.set('language', language);
            }
            
            const response = await fetch(url.toString(), {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    'Accept': 'application/json',
                },
                body: JSON.stringify({listingId, quantity}),
            });
            if(response.ok) {
                const data = await response.json();
                if (data.error === 'timed-out') {
                    return {
                        reserved: [],
                        cartId: cartId,
                        answers: {},
                        pendingPayment: false,
                        orderId: null,
                        listings: [],
                        error: "timed-out"
                    };
                }
                return data;
            } else {
                const cart = await this.getCart(slug, cartId, language);
                return {
                    ...cart,
                    error: "Failed to reserve tickets, please try again.",
                };
            }
        } catch (error) {
            console.error('Failed to reserve listing:', error);
            // reload cart
            return await this.getCart(slug, cartId, language);
        }
    }

    public async checkout(slug: string, cartId: string, formAnswers: object, paymentMethodId: string, language?: string): Promise<{type: "redirect", url: string}|{type: "error", error: string}> {
        const url = new URL(`${this.baseUrl}/marketplace/${slug}/cart/${cartId}/checkout`);
        if (language) {
            url.searchParams.set('language', language);
        }
        
        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({answers: formAnswers, paymentMethodId, redirectAfterPaymentUrl: `${window.location.origin}/marketplace/${slug}/checkout/${cartId}`}),
        });
        const data = await response.json();
        if (data.error === 'timed-out') {
            return {type: "error", error: "Cart has timed out"};
        }
        return data;
    }

    public async joinWaitlist(slug: string, email: string): Promise<void> {
        const response = await fetch(`${this.baseUrl}/marketplace/${slug}/join-waitlist`, {
            method: 'POST',
            body: JSON.stringify({email}),
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
        });
        await response.json();
    }

    public async checkOnWaitlist(slug: string, email: string): Promise<{success: boolean}> {
        const response = await fetch(`${this.baseUrl}/marketplace/${slug}/on-waitlist/${encodeURIComponent(email)}`, {
            headers: {
                'Accept': 'application/json',
            },
        });
        return await response.json();
    }

    public async leaveWaitlist(slug: string, email: string): Promise<void> {
        const response = await fetch(`${this.baseUrl}/marketplace/${slug}/leave-waitlist`, {
            method: 'POST',
            body: JSON.stringify({email}),
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
        });
        await response.json();
    }

    public async validateCart(slug: string, cartId: string, formAnswers: object, language?: string): Promise<{success: boolean, errors?: Record<string, string[]>}> {
        const url = new URL(`${this.baseUrl}/marketplace/${slug}/cart/${cartId}/validate`);
        if (language) {
            url.searchParams.set('language', language);
        }
        
        const response = await fetch(url.toString(), {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'Accept': 'application/json',
            },
            body: JSON.stringify({answers: formAnswers}),
        });
        if (response.status === 422) {
            const data = await response.json();
            return { success: false, errors: data.data || {} };
        }
        if (!response.ok) {
            return { success: false, errors: { general: ["Failed to validate cart"] } };
        }
        return { success: true };
    }

}

